import { useEffect } from 'react';

/**
 * useKeyboardShortcuts - Global keyboard shortcuts for navigation
 * 
 * Usage: Ctrl+1 through Ctrl+8 mapped to views
 * 
 * @param {Object} shortcuts - Map of number key (1-9) to { view, handler, check }
 *   - view: string view name (for display)
 *   - handler: () => void function to call on shortcut
 *   - check: () => bool optional guard (e.g. role check)
 */
const useKeyboardShortcuts = (shortcuts) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle Ctrl + number (ignore if typing in input/textarea)
      if (!e.ctrlKey || e.metaKey) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;

      const num = parseInt(e.key);
      if (isNaN(num) || num < 0 || num > 9) return;

      const shortcut = shortcuts[num];
      if (!shortcut) return;

      // Check if user has access (role guard)
      if (shortcut.check && !shortcut.check()) return;

      e.preventDefault();
      shortcut.handler();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
};

export default useKeyboardShortcuts;
